/**
 * Everything the Orb says, in one place (REDESIGN.md §4).
 *
 * The Orb is presence, not a chatbot: it has no input and never answers. It
 * greets you, tells you the one thing that is true right now, and goes quiet.
 * Keeping the wording here rather than in the component means the voice can be
 * revised without touching the animation.
 *
 * Tone: second person, short, calm. No exclamation marks, no emoji, no
 * gamification vocabulary.
 */

/** A time-aware salutation. The hour is the viewer's own, not the island's. */
export function salutation(firstName, now = new Date()) {
  const hour = now.getHours()

  const greeting =
    hour < 5 ? 'Still awake' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'

  return firstName ? `${greeting}, ${firstName}.` : `${greeting}.`
}

/**
 * The one situational line, in priority order (REDESIGN.md §4):
 * unplayed audio > latest unseen orb message > situational > default.
 *
 * The caller resolves the first two, because only it knows what has been seen;
 * this picks the line for everything below them.
 *
 * @param {{day?: number|null, total?: number|null, daysToNext?: number|null,
 *          onSite?: boolean, hasPlanToday?: boolean}} context
 */
export function situational(context = {}) {
  const { day = null, total = null, daysToNext = null, onSite = false, hasPlanToday = true } = context

  if (onSite && !hasPlanToday) {
    return day ? `Day ${day}. No plan today.` : 'No plan today.'
  }

  if (daysToNext === 0) {
    return 'Something new is waiting for you.'
  }

  if (daysToNext === 1) {
    return 'Next transmission tomorrow.'
  }

  if (daysToNext !== null && daysToNext > 1) {
    return `Next transmission in ${daysToNext} days.`
  }

  if (day !== null && total !== null) {
    return `Day ${day} of your ${total}.`
  }

  return DEFAULT_LINE
}

/** When there is nothing to say, say that — rather than inventing something. */
export const DEFAULT_LINE = 'All quiet. As it should be.'

/** Shown while a transmission is actually playing. */
export const RECEIVING = 'RECEIVING TRANSMISSION'

/**
 * The readout under the greeting. Decorative honesty: it reports the network
 * and the phase of the retreat, and never invents an error.
 *
 * `place` is the retreat's own word for where it happens — island, mountain,
 * house. It used to say ISLAND MODE to everyone, which was true of exactly one
 * retreat and a lie about every other.
 *
 * @param {{online?: boolean, onSite?: boolean, place?: string, sync?: number|null}} context
 */
export function readout(context = {}) {
  const { online = true, onSite = false, sync = null } = context
  // `||`, not a default argument: the journey sends null for a retreat that has
  // no word yet, and a default only answers for undefined.
  const place = context.place || 'retreat'

  if (!online) {
    return 'OFFLINE'
  }

  if (onSite) {
    return `${String(place).toUpperCase()} MODE`
  }

  return sync === null ? 'LINK STABLE' : `LINK STABLE · SYNC ${sync}%`
}
